import React, { ReactNode, createContext, useReducer } from "react";

// eslint-disable-next-line import/no-named-as-default
import produce from "immer";

import {
  BaseLayerState,
  BubbleVisLegendState,
  CreatedState,
  EntitiesInGroupState as EntitiesOnMapState,
  FacilitiesInGroupState,
  FullscreenState,
  HighlightedFeaturesState,
  IsPolygonFilterControlVisibleState,
  MapAlertState,
  MapBoundState,
  MapContextState,
  MapInitializedState,
  MapLoadingSource,
  MapboxglState,
  NoWellsAvailableState,
  SelectedAndHighlightFeatureState,
  SelectedFeaturesState,
  StyleLoadedState,
  UpdateLoadingState
} from "./MapContextState";

export const initialMapContextState: MapContextState = {
  isLoading: false,
  loadingSources: {},
  isStyleLoaded: false,
  isMapInitialized: false,
  noWellsAvailable: false,
  created: false,
  mapbox: undefined,
  bounds: undefined,
  isFullscreen: false,
  isScreenshotMode: false,
  isPolygonFilterControlVisible: false,
  highlightedFeatures: { line: [], point: [], facility: [] },
  selectedFeatures: [],
  entitiesInGroup: {},
  facilitiesInGroup: {},
  baseLayer: "Street",
  mapAlert: "",
  bubbleVisLegend: undefined
};
export const MapContext = createContext(initialMapContextState);

export interface Action<T, Type> {
  type?: Type;
  payload: T;
}
export const MapDispatchContext =
  createContext<React.Dispatch<Action<MapUpdates, "update">>>(undefined);

export type MapUpdates =
  | UpdateLoadingState
  | CreatedState
  | MapInitializedState
  | MapboxglState
  | MapAlertState
  | MapBoundState
  | StyleLoadedState
  | NoWellsAvailableState
  | MapContextState
  | EntitiesOnMapState
  | FacilitiesInGroupState
  | SelectedFeaturesState
  | HighlightedFeaturesState
  | IsPolygonFilterControlVisibleState
  | SelectedAndHighlightFeatureState
  | BaseLayerState
  | BubbleVisLegendState
  | FullscreenState;

export function mapReducer(state: MapContextState, action: Action<MapUpdates, "update">) {
  return produce(state, (draft) => {
    const payload = action.payload as UpdateLoadingState;

    // Handle loading source updates
    if ("loadingSource" in payload && payload.loadingSource !== undefined) {
      const source = payload.loadingSource as MapLoadingSource;
      const isSourceLoading = payload.isLoading ?? false;

      draft.loadingSources = {
        ...draft.loadingSources,
        [source]: isSourceLoading
      };

      // Compute overall isLoading from all sources
      draft.isLoading = Object.values(draft.loadingSources).some(
        (loading) => loading === true
      );
    } else if ("isLoading" in payload && payload.isLoading !== undefined) {
      // legacy support: direct isLoading updates (use General source)
      draft.loadingSources = {
        ...draft.loadingSources,
        [MapLoadingSource.General]: payload.isLoading
      };
      draft.isLoading = Object.values(draft.loadingSources).some(
        (loading) => loading === true
      );
    } else {
      // Other payload updates
      Object.assign(draft, action.payload);
    }
  });
}

export interface MapProviderModel {
  children: ReactNode;
  state?: MapContextState;
}

export function MapProvider({ children, state: overrideState }: MapProviderModel) {
  const [state, dispatch] = useReducer(
    mapReducer,
    overrideState ?? initialMapContextState
  );

  return (
    <MapContext.Provider value={state}>
      <MapDispatchContext.Provider value={dispatch}>
        {children}
      </MapDispatchContext.Provider>
    </MapContext.Provider>
  );
}
